import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { JsonEditorComponent, JsonEditorOptions } from 'ang-jsoneditor';
import { NotificationService } from 'app/core/services/notification.service';
import { Destroyable } from 'app/core/util/destroyable';
import { util } from 'app/core/util/util';

interface Comparison {
  missingKeys: string[];
  unknownKeys: string[];
  emptyKeys: string[];
}

@Component({
  selector: 'admin-i18n-json-editor',
  templateUrl: './i18n-json-editor.html',
  styleUrls: ['./i18n-json-editor.scss'],
})
export class I18nJsonEditor extends Destroyable implements OnInit, OnChanges {
  @Input() json: object;
  @Input() referenceJson: object;
  @Input() loading: boolean;
  @Input() saving: boolean;
  @Input() enableSave = false;
  @Output() changed = new EventEmitter<void>();
  @Output() save = new EventEmitter<string>();
  @ViewChild('editor', { static: false }) editor: JsonEditorComponent;

  editorOptions: JsonEditorOptions;
  defaultTranslation: string; // value of nod in default.json
  emptyTranslationsCount = 0;
  editingEmptyTranslations = false;
  jsonWithEmptyTranslations: object;

  private defaultTranslationPosition: { top: string; left: string; x: number; y: number };
  private entryMouseY: number; // used to close defaultTranslation-tooltip when leaving node

  constructor(private notificationService: NotificationService) {
    super();
  }

  ngOnInit() {
    this.setJsonEditorOptions();
  }

  ngOnChanges(changes) {
    if (this.json && changes.json) {
      // make a clone copy so that it's editable (and not frozen because source = store)
      this.json = util.jsonDeepCopy(this.json);
    }

    if (this.json && this.referenceJson && (changes.json || changes.referenceJson)) {
      // remove unused keys, add missing keys
      this.syncWithReference();
    }
  }

  async saveClicked() {
    if (this.enableSave && !this.saving) {
      const editorJson = this.editor.getEditor();
      const errors = editorJson.validate();
      if (!errors || !errors.length) {
        const changedJson = this.editor.get();
        this.save.emit(JSON.stringify(changedJson));
      } else {
        // should not occur, but let's put this in the code and see if it's a valid statement
        this.notificationService.showModal({
          type: 'error',
          message: 'JSON is invalid.  Please contact support to analyse this problem.',
          subMessages: errors,
        });
      }
    }
  }

  /**
   * event from jsonEditor.  only triggered when hovering over a new node (not white space)
   * @param field
   * @private
   */
  private nodeHovered(field: { field: string; path: string[]; value?: string }, mouseEvent: MouseEvent) {
    if (field.value !== undefined) {
      this.entryMouseY = this.defaultTranslationPosition?.y || 0;
      this.setDefaultValue(field.path);

      if (mouseEvent.type === 'click' && mouseEvent.altKey) {
        // copy value
        (mouseEvent.target as HTMLDivElement).innerText = this.defaultTranslation;
      }

    } else {
      this.defaultTranslation = null;
    }
  }

  /**
   * event from div. always triggered.  is a way to hide tooltip if leaving a node, moving into white space
   * @param $event
   */
  mouseMove($event: MouseEvent) {
    this.defaultTranslationPosition = {
      top: $event.clientY + 20 + 'px', // add some offset
      left: $event.clientX - 50 + 'px', // add some offset
      x: $event.clientX, // keep original values
      y: $event.clientY, // keep original values
    };
    // clear defaultValue if leaving the node
    if (Math.abs(this.entryMouseY - this.defaultTranslationPosition.y) > 50) {
      this.defaultTranslation = null;
    }
  }

  /**
   * User can click on 'filter on empty items' button and then only the items with null values are in the editor
   */
  filterOnEmptyTranslations() {
    this.editingEmptyTranslations = true;
    this.json = this.editor.get();
    let flattenedJson = util.flattenJson(this.json);
    const keys = Object.keys(flattenedJson);
    const keysWithEmptyValues = keys.filter(key => flattenedJson[key] === null);
    this.jsonWithEmptyTranslations = util.unflattenJson(keysWithEmptyValues.reduce((result, key) => {
      result[key] = null;
      return result;
    }, {}));
  }

  /**
   * After editing items with null value, user can return to the whole json
   */
  clearFilter() {
    const changedJson = this.editor.get();
    this.editingEmptyTranslations = false;
    let flattenedJson = util.flattenJson(this.json);
    let flattenedJsonPart = util.flattenJson(changedJson);
    flattenedJson = {
      ...flattenedJson,
      ...flattenedJsonPart
    };
    this.json = util.unflattenJson(flattenedJson);
    // @ts-ignore
    this.emptyTranslationsCount = Object.values(flattenedJson).reduce((count, value) => count + (value === null ? 1 : 0), 0);
  }


  /**
   * for a given path, look up value in default.json object and store in property
   * @param path
   * @param reference
   * @private
   */
  private setDefaultValue(path: string[], reference: object | string = this.referenceJson) {
    if (path?.length) {
      return this.setDefaultValue(path.slice(1), reference && reference[path[0]]);
    }
    if (typeof reference === 'string') {
      this.defaultTranslation = reference;
    } else {
      this.defaultTranslation = null;
    }
  }

  private setJsonEditorOptions() {
    this.editorOptions = new JsonEditorOptions();
    // @ts-ignore
    this.editorOptions.onEvent = (field, mouseEvent) => this.nodeHovered(field, mouseEvent);
    this.editorOptions.mode = 'form';
    this.editorOptions.navigationBar = true;
    this.editorOptions.mainMenuBar = true;
    this.editorOptions.sortObjectKeys = true;
    this.editorOptions.enableTransform = false;
    // @ts-ignore
    this.editorOptions.onCreateMenu = ((items, node) => {
      return [
        ...(items || []),
        {
          text: 'Use default value',
          title: 'Set value to the default value',
          click: () => {
            debugger
          },
        }
      ];
    })
  }

  private syncWithReference() {
    this.editingEmptyTranslations = false;
    if (!this.json || !this.referenceJson || Object.keys(this.referenceJson).length === 0) {
      return;
    }
    const diffs = this.compareJsons(this.json, this.referenceJson);
    this.emptyTranslationsCount = diffs.emptyKeys.length;
    this.showToastrMessage(diffs);
    for (let key of diffs.unknownKeys) {
      this.removeKey(key.split('.'), this.json);
    }
    for (let key of diffs.missingKeys) {
      this.addKey(key.split('.'), this.json);
    }
    if (diffs.unknownKeys.length || diffs.missingKeys.length) {
      this.json = util.jsonDeepCopy(this.json); // trigger reload
    }
  }

  private compareJsons(json: any, reference: any): Comparison {
    const refKeys = Object.keys(util.flattenJson(reference));
    let flattenedJson = util.flattenJson(json);
    const keys = Object.keys(flattenedJson);
    const missingKeys = refKeys.filter(key => keys.indexOf(key) === -1);
    const unknownKeys = keys.filter(key => refKeys.indexOf(key) === -1);
    const emptyKeys = [...missingKeys, ...keys.filter(key => flattenedJson[key] === null)];
    return { missingKeys, unknownKeys, emptyKeys };
  }

  private removeKey(keys: string[], json: object) {
    if (keys.length > 1) {
      const part = json[keys[0]];
      this.removeKey(keys.slice(1), part);
      if (Object.keys(part).length === 0) {
        delete json[keys[0]];
      }
    } else {
      delete json[keys[0]];
    }
  }

  private addKey(keys: string[], json: object) {
    if (keys.length > 1) {
      if (!json[keys[0]]) {
        json[keys[0]] = {};
      }
      this.addKey(keys.slice(1), json[keys[0]]);
    } else {
      json[keys[0]] = null;
    }
  }

  private showToastrMessage(diffs: Comparison) {
    let message = '';
    if (diffs.unknownKeys?.length) {
      message += `${diffs.unknownKeys.length} row${diffs.unknownKeys.length > 1 ? 's' : ''} has been removed since they are not used anymore.  `;
    }
    if (diffs.missingKeys?.length) {
      message += `${diffs.missingKeys.length} row${diffs.missingKeys.length > 1 ? 's' : ''} are not translated in the selected language.  `;
    }
    if (message !== '') {
      console.info(message, diffs);
      // this.notificationService.showToastr({
      //   message,
      //   type: 'info',
      // });
    }
  }
}
